import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import Cropper, { type Area, type Point } from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import UploadSuccessHintIconSvg from '../../../assets/icons/icon-registration-photo-success-hint.svg?react';
import profileAvatarDefaultImage from '../../../assets/images/profile-avatar-default.png';
import {
  type EmployeeDashboard,
  type EmployeeProfile,
  uploadEmployeeAvatar,
} from '../../../shared/api/employee.service';
import { resolveRenderableAvatarUrl } from '../../../shared/model';
import {
  BaseButton,
  PrimaryButton,
  mainHeadingTypographyStyles,
  nonSelectableSurfaceStyles,
  rubikTypographyStyles,
} from '../../../shared/ui';

import { createCroppedAvatarBlob } from './avatar-editor-crop';

export type AvatarEditorVariant = 'registration' | 'profile';

type AvatarEditorFlowProps = {
  initialAvatarUrl?: string | null;
  initialPhoto?: File | null;
  onComplete: () => void;
  variant: AvatarEditorVariant;
};

type EditorStage = 'select' | 'crop' | 'preview';

const COPY_KEY_PREFIX_BY_VARIANT = {
  profile: 'myPage.avatarEditor',
  registration: 'registrationPhoto',
} as const;
const CROP_VIEWPORT_SIZE_PX = 220;
const DEFAULT_CROP: Point = { x: 0, y: 0 };
const DEFAULT_ZOOM = 1;
const EMPLOYEE_DASHBOARD_QUERY_KEY = ['employee', 'dashboard'] as const;
const EMPLOYEE_PROFILE_QUERY_KEY = ['employee', 'profile'] as const;

function resolveAvatarFileName(fileName: string, mimeType: string): string {
  const normalizedBaseName =
    fileName.replace(/\.[^.]+$/, '').trim() || 'avatar';

  if (mimeType === 'image/jpeg') {
    return `${normalizedBaseName}.jpg`;
  }

  return `${normalizedBaseName}.webp`;
}

function resolveAvatarSource(avatarUrl?: string | null) {
  return resolveRenderableAvatarUrl(avatarUrl) ?? profileAvatarDefaultImage;
}

const Screen = styled.div.attrs({
  'data-ui': 'avatar-editor-flow',
})`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  ${nonSelectableSurfaceStyles};

  & > [data-ui='avatar-editor-heading'] {
    margin-left: ${({ theme }) => theme.scale.px(31)};
    margin-bottom: ${({ theme }) => theme.scale.px(108)};
  }

  & > [data-ui='avatar-editor-stage'] {
    align-self: center;
    margin-bottom: ${({ theme }) => theme.scale.px(15)};
  }

  & > [data-ui='avatar-editor-status'] {
    align-self: center;
    margin-bottom: auto;
  }
`;

const Heading = styled.div.attrs({
  'data-ui': 'avatar-editor-heading',
})`
  width: ${({ theme }) => theme.scale.px(341)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const Title = styled.h1`
  ${mainHeadingTypographyStyles};
  line-height: ${({ theme }) => theme.scale.px(28)};
  white-space: pre-line;
`;

const Subtitle = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
  white-space: pre-line;
`;

const AvatarStage = styled.div.attrs({
  'data-ui': 'avatar-editor-stage',
})`
  width: ${({ theme }) => theme.scale.px(CROP_VIEWPORT_SIZE_PX)};
  height: ${({ theme }) => theme.scale.px(CROP_VIEWPORT_SIZE_PX)};
  position: relative;
  border: ${({ theme }) =>
    `${theme.scale.px(2)} solid ${theme.colors.brand.primary}`};
  border-radius: 50%;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface.subtle};
`;

const CropSurface = styled.div`
  position: absolute;
  inset: 0;
`;

const PreviewImage = styled.img.attrs({
  alt: '',
  'aria-hidden': true,
  draggable: 'false',
})`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
`;

const StatusText = styled.p.attrs({
  'data-ui': 'avatar-editor-status',
})`
  ${rubikTypographyStyles};
  width: ${({ theme }) => theme.scale.px(220)};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
  text-align: center;
  white-space: pre-line;
`;

const PreviewStatus = styled.div.attrs({
  'data-ui': 'avatar-editor-status',
})`
  width: ${({ theme }) => theme.scale.px(220)};
  display: grid;
  grid-template-columns: ${({ theme }) =>
    `${theme.scale.px(15)} minmax(0, 1fr)`};
  align-items: start;
  column-gap: ${({ theme }) => theme.scale.px(7)};
`;

const PreviewStatusText = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
  white-space: pre-line;
`;

const UploadSuccessHintIcon = styled(UploadSuccessHintIconSvg).attrs({
  'aria-hidden': true,
  focusable: 'false',
})`
  width: ${({ theme }) => theme.scale.px(15)};
  height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.support.success};
`;

const UploadProgress = styled.div.attrs({
  'data-ui': 'avatar-editor-status',
})`
  width: ${({ theme }) => theme.scale.px(220)};
  display: grid;
  gap: ${({ theme }) => theme.scale.px(7)};
`;

const UploadProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.scale.px(8)};
`;

const UploadProgressLabel = styled.p`
  ${rubikTypographyStyles};
  margin: 0;
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: ${({ theme }) => theme.scale.px(18)};
`;

const UploadProgressTrack = styled.div`
  width: 100%;
  height: ${({ theme }) => theme.scale.px(6)};
  border-radius: ${({ theme }) => theme.scale.px(3)};
  background: ${({ theme }) => theme.colors.surface.tertiary};
  overflow: hidden;
`;

const UploadProgressFill = styled.div<{ $progressPercent: number }>`
  width: ${({ $progressPercent }) => `${$progressPercent}%`};
  height: 100%;
  border-radius: inherit;
  background: ${({ theme }) => theme.colors.brand.primary};
  transition: width 180ms ease;
`;

const Footer = styled.div.attrs({
  'data-ui': 'avatar-editor-actions',
})`
  flex-shrink: 0;
  display: grid;
  justify-items: center;
  row-gap: ${({ theme }) => theme.scale.px(13)};
  padding-bottom: ${({ theme }) => theme.scale.px(8)};
`;

const FooterSecondary = styled.div.attrs({
  'data-ui': 'avatar-editor-actions-secondary',
})`
  width: 100%;
  min-height: ${({ theme }) => theme.scale.px(36)};
  display: grid;
  justify-items: center;
  align-content: start;
`;

const PrimaryAction = styled(PrimaryButton)`
  align-self: center;

  &:disabled {
    opacity: 0.55;
  }
`;

const SecondaryAction = styled(BaseButton)`
  ${rubikTypographyStyles};
  font-size: ${({ theme }) => theme.scale.px(15)};
  font-weight: 400;
  line-height: 1.25;
  text-decoration: underline;

  &:disabled {
    opacity: 0.55;
  }
`;

const HiddenPhotoInput = styled.input.attrs({
  accept: 'image/*',
  tabIndex: -1,
  type: 'file',
})`
  display: none;
`;

export function AvatarEditorFlow({
  initialAvatarUrl = null,
  initialPhoto = null,
  onComplete,
  variant,
}: AvatarEditorFlowProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();
  const copyKeyPrefix = COPY_KEY_PREFIX_BY_VARIANT[variant];
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(initialPhoto);
  const [editorStage, setEditorStage] = useState<EditorStage>(
    initialPhoto ? 'crop' : 'select',
  );
  const [crop, setCrop] = useState<Point>(DEFAULT_CROP);
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedAvatarBlob, setCroppedAvatarBlob] = useState<Blob | null>(null);
  const [isPreparingPreview, setIsPreparingPreview] = useState(false);
  const [sourcePhotoUrl, setSourcePhotoUrl] = useState<string | null>(null);
  const [previewPhotoUrl, setPreviewPhotoUrl] = useState<string | null>(null);
  const [statusErrorKey, setStatusErrorKey] = useState<string | null>(null);
  const [uploadProgressPercent, setUploadProgressPercent] = useState(0);
  const avatarUpload = useMutation({
    mutationFn: uploadEmployeeAvatar,
    onSuccess: avatarUrl => {
      queryClient.setQueryData<EmployeeProfile | undefined>(
        EMPLOYEE_PROFILE_QUERY_KEY,
        previousProfile =>
          previousProfile
            ? {
                ...previousProfile,
                avatarUrl,
              }
            : previousProfile,
      );
      queryClient.setQueryData<EmployeeDashboard | undefined>(
        EMPLOYEE_DASHBOARD_QUERY_KEY,
        previousDashboard =>
          previousDashboard
            ? {
                ...previousDashboard,
                profile: {
                  ...previousDashboard.profile,
                  avatarUrl,
                },
              }
            : previousDashboard,
      );

      void queryClient.invalidateQueries({
        queryKey: EMPLOYEE_PROFILE_QUERY_KEY,
        refetchType: 'inactive',
      });
      void queryClient.invalidateQueries({
        queryKey: EMPLOYEE_DASHBOARD_QUERY_KEY,
        refetchType: 'inactive',
      });
    },
  });

  useEffect(() => {
    if (!selectedPhoto) {
      setSourcePhotoUrl(null);

      return undefined;
    }

    const nextSourcePhotoUrl = URL.createObjectURL(selectedPhoto);

    setSourcePhotoUrl(nextSourcePhotoUrl);

    return () => {
      URL.revokeObjectURL(nextSourcePhotoUrl);
    };
  }, [selectedPhoto]);

  useEffect(() => {
    if (!croppedAvatarBlob) {
      setPreviewPhotoUrl(null);

      return undefined;
    }

    const nextPreviewPhotoUrl = URL.createObjectURL(croppedAvatarBlob);

    setPreviewPhotoUrl(nextPreviewPhotoUrl);

    return () => {
      URL.revokeObjectURL(nextPreviewPhotoUrl);
    };
  }, [croppedAvatarBlob]);

  useEffect(() => {
    if (!selectedPhoto) {
      setEditorStage('select');
      setCrop(DEFAULT_CROP);
      setZoom(DEFAULT_ZOOM);
      setCroppedAreaPixels(null);
      setCroppedAvatarBlob(null);
      setStatusErrorKey(null);
      setUploadProgressPercent(0);

      return;
    }

    setEditorStage('crop');
    setCrop(DEFAULT_CROP);
    setZoom(DEFAULT_ZOOM);
    setCroppedAreaPixels(null);
    setCroppedAvatarBlob(null);
    setStatusErrorKey(null);
    setUploadProgressPercent(0);
  }, [selectedPhoto]);

  const handleChoosePhoto = () => {
    photoInputRef.current?.click();
  };

  const handlePhotoChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSelectedPhoto = event.currentTarget.files?.[0];

    event.currentTarget.value = '';

    if (!nextSelectedPhoto) {
      return;
    }

    setSelectedPhoto(nextSelectedPhoto);
  };

  const handleConfirmCrop = async () => {
    if (!sourcePhotoUrl || !croppedAreaPixels) {
      return;
    }

    setStatusErrorKey(null);
    setIsPreparingPreview(true);

    try {
      const nextCroppedAvatarBlob = await createCroppedAvatarBlob(
        sourcePhotoUrl,
        croppedAreaPixels,
      );

      setCroppedAvatarBlob(nextCroppedAvatarBlob);
      setEditorStage('preview');
    } catch {
      setStatusErrorKey(`${copyKeyPrefix}.prepareFailure`);
    } finally {
      setIsPreparingPreview(false);
    }
  };

  const handleUploadAndComplete = async () => {
    if (!croppedAvatarBlob || !selectedPhoto) {
      return;
    }

    setStatusErrorKey(null);
    setUploadProgressPercent(0);

    try {
      await avatarUpload.mutateAsync({
        avatar: croppedAvatarBlob,
        fileName: resolveAvatarFileName(
          selectedPhoto.name,
          croppedAvatarBlob.type,
        ),
        onUploadProgress: setUploadProgressPercent,
      });

      onComplete();
    } catch {
      setStatusErrorKey(`${copyKeyPrefix}.uploadFailure`);
    }
  };

  const isPreviewStage = editorStage === 'preview' && previewPhotoUrl !== null;
  const isSelectStage = editorStage === 'select';
  const isBusy = isPreparingPreview || avatarUpload.isPending;
  const cropViewportSize = theme.scale.value(CROP_VIEWPORT_SIZE_PX);

  return (
    <Screen>
      <Heading>
        <Title>{t(`${copyKeyPrefix}.title`)}</Title>
        <Subtitle>{t(`${copyKeyPrefix}.subtitle`)}</Subtitle>
      </Heading>

      <AvatarStage>
        {isPreviewStage ? (
          <PreviewImage src={previewPhotoUrl} />
        ) : sourcePhotoUrl ? (
          <CropSurface>
            <Cropper
              aspect={1}
              crop={crop}
              cropShape="round"
              cropSize={{
                height: cropViewportSize,
                width: cropViewportSize,
              }}
              image={sourcePhotoUrl}
              objectFit="cover"
              restrictPosition={false}
              showGrid={false}
              zoom={zoom}
              zoomWithScroll
              onCropChange={setCrop}
              onCropComplete={(_, nextCroppedAreaPixels) => {
                setCroppedAreaPixels(nextCroppedAreaPixels);
              }}
              onZoomChange={setZoom}
            />
          </CropSurface>
        ) : (
          <PreviewImage src={resolveAvatarSource(initialAvatarUrl)} />
        )}
      </AvatarStage>

      {avatarUpload.isPending ? (
        <UploadProgress>
          <UploadProgressHeader>
            <UploadProgressLabel>
              {t(`${copyKeyPrefix}.uploadingHint`)}
            </UploadProgressLabel>
            <UploadProgressLabel>{uploadProgressPercent}%</UploadProgressLabel>
          </UploadProgressHeader>
          <UploadProgressTrack>
            <UploadProgressFill $progressPercent={uploadProgressPercent} />
          </UploadProgressTrack>
        </UploadProgress>
      ) : statusErrorKey ? (
        <StatusText>{t(statusErrorKey)}</StatusText>
      ) : isPreviewStage ? (
        <PreviewStatus>
          <UploadSuccessHintIcon />
          <PreviewStatusText>
            {t(`${copyKeyPrefix}.successHint`)}
          </PreviewStatusText>
        </PreviewStatus>
      ) : (
        <StatusText>
          {t(
            isSelectStage
              ? `${copyKeyPrefix}.selectHint`
              : `${copyKeyPrefix}.cropHint`,
          )}
        </StatusText>
      )}

      <Footer>
        <PrimaryAction
          type="button"
          disabled={
            isBusy || (!isSelectStage && !isPreviewStage && !croppedAreaPixels)
          }
          onClick={
            isSelectStage
              ? handleChoosePhoto
              : isPreviewStage
              ? handleUploadAndComplete
              : handleConfirmCrop
          }
        >
          {t(
            isSelectStage
              ? `${copyKeyPrefix}.chooseCta`
              : isPreviewStage
              ? `${copyKeyPrefix}.continueCta`
              : `${copyKeyPrefix}.confirmCropCta`,
          )}
        </PrimaryAction>
        <FooterSecondary>
          {isSelectStage ? null : (
            <SecondaryAction
              type="button"
              disabled={isBusy}
              onClick={handleChoosePhoto}
            >
              {t(`${copyKeyPrefix}.reselectCta`)}
            </SecondaryAction>
          )}
        </FooterSecondary>
      </Footer>

      <HiddenPhotoInput ref={photoInputRef} onChange={handlePhotoChange} />
    </Screen>
  );
}
